/**
 * MarkerLayer — renders map markers for the active scene's pins.
 * Only the scene's active location (activePinId) is shown; other pins are hidden.
 * Support locations attached to the active location are also rendered.
 */
import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api.js';
import { useMapStore } from '../../stores/mapStore.js';
import { useUiStore } from '../../stores/uiStore.js';
import type { Pin, LocationSupport } from '../../types/index.js';

const PIN_COLORS: Record<string, string> = {
  main: '#4361ee',
  basecamp: '#2d9e5f',
  parking: '#f59e0b',
  hospital: '#ef4444',
};

const PIN_ICONS: Record<string, string> = {
  main: '📍',
  basecamp: '🏠',
  parking: '🅿️',
  hospital: '🏥',
};

interface MarkerLayerProps {
  map: mapboxgl.Map;
}

export function MarkerLayer({ map }: MarkerLayerProps) {
  const { activeScene, setSelectedPin, flyTo, setDetailPin } = useMapStore();
  const { openModal } = useUiStore();
  const mainMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const supportMarkersRef = useRef<mapboxgl.Marker[]>([]);

  const { data } = useQuery({
    queryKey: ['pins', activeScene?.id],
    queryFn: async () => {
      if (!activeScene) return { pins: [] };
      const result = await api.get<{ pins: Pin[] }>(`/pins?sceneId=${activeScene.id}&limit=200`);
      return result.success ? result.data : { pins: [] };
    },
    enabled: !!activeScene,
  });

  // Determine which pin is the active one for this scene
  const allPins = data?.pins ?? [];
  const activePinId = activeScene?.activePinId;
  // Fall back to the first main pin if no explicit active pin is set
  const activePin = activePinId
    ? allPins.find(p => p.id === activePinId)
    : allPins.find(p => p.pinType === 'main') ?? allPins[0];

  // Fetch support locations for the active pin's library location
  const { data: supports = [] } = useQuery({
    queryKey: ['location-supports', activePin?.locationId],
    queryFn: async () => {
      const result = await api.get<{ supports: LocationSupport[] }>(`/locations/${activePin!.locationId}/supports`);
      return result.success ? result.data.supports : [];
    },
    enabled: !!activePin?.locationId,
  });

  // Main pin marker
  useEffect(() => {
    mainMarkersRef.current.forEach(m => m.remove());
    mainMarkersRef.current = [];

    if (!activePin) return;

    const el = document.createElement('div');
    el.className = 'cursor-pointer select-none text-xl transition-transform hover:scale-125';
    el.textContent = PIN_ICONS[activePin.pinType] ?? '📍';
    el.title = activePin.title;
    el.style.filter = `drop-shadow(0 0 4px ${PIN_COLORS[activePin.pinType] ?? '#4361ee'})`;

    el.addEventListener('click', () => {
      setSelectedPin(activePin.id);
      setDetailPin(activePin);
      flyTo(activePin.lng, activePin.lat);
    });

    const marker = new mapboxgl.Marker({ element: el })
      .setLngLat([activePin.lng, activePin.lat])
      .setPopup(
        new mapboxgl.Popup({ offset: 25, closeButton: false }).setHTML(
          `<div class="text-sm font-semibold">${activePin.title}</div><div class="text-xs text-gray-500">${activePin.address}</div>`,
        ),
      )
      .addTo(map);

    mainMarkersRef.current.push(marker);

    return () => { mainMarkersRef.current.forEach(m => m.remove()); mainMarkersRef.current = []; };
  }, [activePin, map, setSelectedPin, openModal, flyTo, setDetailPin]);

  // Support location markers (amber dots)
  useEffect(() => {
    supportMarkersRef.current.forEach(m => m.remove());
    supportMarkersRef.current = [];

    supports.forEach((sup: LocationSupport) => {
      const el = document.createElement('div');
      el.title = sup.name;
      el.style.cssText = 'width:14px;height:14px;background:#f59e0b;border-radius:50%;border:2px solid white;box-shadow:0 0 8px rgba(245,158,11,0.8);cursor:pointer;';

      el.addEventListener('click', () => flyTo(sup.lng, sup.lat));

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([sup.lng, sup.lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 20, closeButton: false }).setHTML(
            `<div class="text-sm font-semibold">${sup.name}</div><div class="text-xs text-gray-500">${sup.address}</div>`,
          ),
        )
        .addTo(map);

      supportMarkersRef.current.push(marker);
    });

    return () => { supportMarkersRef.current.forEach(m => m.remove()); supportMarkersRef.current = []; };
  }, [supports, map, flyTo]);

  return null;
}
